import React, { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { DataTable, Menu, MenuItem, StatusPill } from '../patterns';
import { formatMonthName } from '../../dateFormat';
import { NutritionProgramme, SoilRound } from '../../services/nutritionService';
import { nutritionSessionService } from '../../services/nutritionSessionService';
import NutritionConvertRoundModal from './NutritionConvertRoundModal';

interface Props {
  program: NutritionProgramme;
  /**
   * The SINGLE source of truth for "which programme" throughout this
   * component — every service call (getSessions/convertRound), the container
   * testid, and every navigation target uses this, never `program._id`.
   * Callers always have the two equal, but `program` is sometimes a
   * caller-assembled partial/local object (Task 9's page merges live
   * `rounds` onto a frozen snapshot), so this panel must not assume
   * `program._id` is trustworthy — one id, one field to read it from.
   */
  programmeId: string;
  /** Navigate to the round DEFINITION editor for this round id. */
  onEditRound: (roundId: string) => void;
  /** Drop this round from local state; nothing is deleted server-side until Save. */
  onRemoveRound: (roundId: string) => void;
  /** Hides "Remove round" — set by a locked (active) programme. */
  readOnly?: boolean;
  onRefresh?: () => void;
}

interface RoundRow { rid: string; round: SoilRound; idx: number; label: string; }

/**
 * Inline panel rendered under an expanded program row on the Nutrition
 * Programs list, and (Task 9) embedded in the programme editor's rounds
 * section. Read-only summary of the program's rounds + a per-round 3-dot
 * menu.
 *
 * Slice 3: rounds gain "Convert to Session" (opens convert modal) and
 * "View sessions" (navigates to the round's dedicated page).
 *
 * Task 9 (F4): "View sessions" goes to the round's dedicated page
 * (FoliarRoundPage). `onViewRoundDetails` is removed: inside the programme
 * editor it pointed at the page the user is already on. `Edit round` /
 * `Remove round` replace it, driven by the new `onEditRound`/`onRemoveRound`
 * callbacks so the owning page controls navigation and local-state mutation.
 *
 * F6 (#417 S5): legacy soil rounds are retired — every round handled here is
 * foliar (the boot migration rewrites `rounds[].type` to `'foliar'`
 * unconditionally), so the type-branching this panel used to do for soil
 * rounds (convert endpoint, view-sessions target, type badge) is gone.
 *
 * GC 8 / defect class #456/#457: the 3-dot menu is rendered by ONE shared
 * `renderMenu(row, testId)` used by both the desktop table column and the
 * `mobileCardRow`, so portrait phones (which only ever see the card view)
 * keep the menu reachable. Distinct testids per call site
 * (`round-menu-table-*` / `round-menu-card-*`) because jsdom renders both
 * simultaneously and a shared id would make `getByTestId` ambiguous.
 */
const NutritionProgramRoundsPanel: React.FC<Props> = ({
  program,
  programmeId,
  onEditRound,
  onRemoveRound,
  readOnly,
  onRefresh,
}) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const rounds = program.rounds || [];

  // Convert-round modal state
  const [convertRound, setConvertRound] = useState<SoilRound | null>(null);
  const [converting, setConverting] = useState(false);
  const [convertError, setConvertError] = useState<string | null>(null);
  // orchardNos that already have a non-deleted session for the round being
  // converted — hidden from the modal.
  const [convertTakenIds, setConvertTakenIds] = useState<string[]>([]);
  const openRoundRef = useRef<string | null>(null);

  if (rounds.length === 0) {
    return <div className="ntr-muted small p-2">{t('nutrition.programme.rounds.noRounds')}</div>;
  }

  const rows: RoundRow[] = rounds.map((r, idx) => ({
    rid: r.roundId,
    round: r,
    idx,
    label: r.name || `${t('nutrition.programme.rounds.roundName')} ${idx + 1}`,
  }));

  const productSummary = (r: SoilRound): string => {
    const names = new Set<string>();
    (r.groups || []).forEach(g => (g.products || []).forEach(p => names.add(p.productName || p.productId)));
    return names.size ? Array.from(names).join(', ') : '—';
  };

  const handleConvertOpen = (round: SoilRound) => {
    setConvertError(null);
    setConvertTakenIds([]);
    setConvertRound(round);
    openRoundRef.current = round.roundId;
    // Fetch the round's existing (non-deleted) sessions so the modal can hide
    // orchards/groups that already have one. getSessions returns only
    // non-deleted sessions, so soft-deleting a session frees its slot again.
    nutritionSessionService
      .getSessions(programmeId, round.roundId)
      .then((summaries) => {
        if (openRoundRef.current !== round.roundId) return; // stale fetch
        const taken = summaries
          .filter((s) => s.session.status !== 'cancelled')
          .map((s) => s.session.orchardNo)
          .filter((id): id is string => !!id);
        setConvertTakenIds(taken);
      })
      .catch(() => {
        // Degrade gracefully: the backend convert still skips already-converted
        // ids, so a fetch failure just means the modal shows everything.
      });
  };

  const handleConvertCancel = () => {
    setConvertRound(null);
    setConvertError(null);
    setConvertTakenIds([]);
    openRoundRef.current = null;
  };

  const handleConvertConfirm = async (ids: string[]) => {
    if (!convertRound) return;
    setConverting(true);
    setConvertError(null);
    try {
      await nutritionSessionService.convertRound(programmeId, convertRound.roundId, ids);
      setConvertRound(null);
      setConvertTakenIds([]);
      openRoundRef.current = null;
      onRefresh?.();
    } catch {
      setConvertError(t('nutrition.sessions.convertModal.error'));
    } finally {
      setConverting(false);
    }
  };

  // Every round is foliar (F6 retired legacy soil rounds — the boot migration
  // rewrites rounds[].type to 'foliar' unconditionally, and no soil round can
  // exist). Foliar rounds have a dedicated round page (FoliarRoundPage).
  const handleViewSessions = (round: SoilRound) => {
    navigate(`/nutrition/foliar/programmes/${programmeId}/rounds/${round.roundId}`);
  };

  // "Remove round" sits directly under "Edit round" in the same 3-dot menu —
  // one row apart from a routine action, and destructive on the owning page's
  // NEXT save (the round disappears from the programme, orphaning every session
  // already created for it, which this component cannot delete). window.confirm
  // matches the programme-level Delete on FoliarProgrammeEditorPage rather than
  // introducing a second confirmation idiom, and the copy names the
  // consequence: the sessions are left behind, not removed.
  const confirmRemoveRound = (row: RoundRow) => {
    if (!window.confirm(t('nutrition.programme.rounds.removeRoundConfirm', { name: row.label }))) return;
    onRemoveRound(row.rid);
  };

  // Shared by the desktop <table> actions column AND the mobileCardRow (GC 8):
  // one render function so both stay in lockstep and portrait phones (which
  // only ever see the card view) keep the menu reachable.
  const renderMenu = (row: RoundRow, testId: string) => (
    <span data-testid={testId} onClick={(e) => e.stopPropagation()}>
      <Menu
        placement="bottom-end"
        trigger={
          <button
            type="button"
            className="pat-menu__trigger"
            aria-label={t('nutrition.programme.rounds.roundActions', { name: row.label })}
          >
            &#8942;
          </button>
        }
      >
        <MenuItem onClick={() => onEditRound(row.rid)}>
          {t('nutrition.programme.rounds.editRound')}
        </MenuItem>
        <MenuItem onClick={() => handleConvertOpen(row.round)}>
          {t('nutrition.sessions.convertModal.menuItem')}
        </MenuItem>
        <MenuItem onClick={() => handleViewSessions(row.round)}>
          {t('nutrition.sessions.viewSessions')}
        </MenuItem>
        {!readOnly && (
          <MenuItem onClick={() => confirmRemoveRound(row)} tone="danger">
            {t('nutrition.programme.rounds.removeRound')}
          </MenuItem>
        )}
      </Menu>
    </span>
  );

  return (
    <>
      <div className="ntr-rounds-panel" data-testid={`nutrition-rounds-${programmeId}`}>
        <DataTable<RoundRow>
          rows={rows}
          rowKey={(row) => row.rid}
          columns={[
            { key: 'name', label: t('nutrition.programme.rounds.roundName'), render: (row) => row.label },
            {
              key: 'type', label: t('nutrition.programme.rounds.type'),
              render: () => (
                <StatusPill variant="info">
                  {t('nutrition.programme.rounds.typeFoliar')}
                </StatusPill>
              ),
            },
            {
              key: 'month', label: t('nutrition.programme.rounds.month'),
              render: (row) => formatMonthName(row.round.month, i18n.language),
            },
            {
              key: 'water', label: t('nutrition.programme.rounds.water'), align: 'right',
              render: (row) => row.round.waterRatePerHa ?? '—',
            },
            { key: 'products', label: t('nutrition.programme.rounds.products'), render: (row) => productSummary(row.round) },
            {
              key: 'actions', label: '', align: 'right',
              render: (row) => renderMenu(row, `round-menu-table-${row.rid}`),
            },
          ]}
          mobileCardRow={(row) => (
            <div className="ntr-proglist-card">
              <div className="ntr-proglist-card__body">
                <strong>{row.label}</strong>{' '}
                <StatusPill variant="info">
                  {t('nutrition.programme.rounds.typeFoliar')}
                </StatusPill>
                <div className="ntr-fst-card-meta">
                  {t('nutrition.programme.rounds.month')}: {formatMonthName(row.round.month, i18n.language)} ·{' '}
                  {t('nutrition.programme.rounds.water')}: {row.round.waterRatePerHa ?? '—'} ·{' '}
                  {productSummary(row.round)}
                </div>
              </div>
              <div className="ntr-proglist-card__actions">
                {renderMenu(row, `round-menu-card-${row.rid}`)}
              </div>
            </div>
          )}
        />
      </div>
      <NutritionConvertRoundModal
        show={!!convertRound}
        round={convertRound}
        onConfirm={handleConvertConfirm}
        onCancel={handleConvertCancel}
        submitting={converting}
        error={convertError}
        unavailableIds={convertTakenIds}
      />
    </>
  );
};

export default NutritionProgramRoundsPanel;
